import { acknowledgmentValue, answerToText, validateAnswer } from './answers';
import { formatEventDatesLabel, localTodayIso } from './dates';
import { adultAgeOf, fullAddress, isMinor, participantName, participantWard } from './builtin';
import { builtinSourceParts } from './columns';
import { pdfRequiredSigners } from './pdfSigners';
import { activeQuestionAnswers } from './questionRouting';
import { isValidDate } from './validate';
import { PDF_BOOLEAN_BUILTINS, pdfQuestion, pdfSourceType, samePdfSource, validatePdfBindings, type PdfBindingEvent, type PdfBindingIssue } from './pdfBindingValidation';
import type { BuiltinAnswers, CustomAnswer, Field, PdfDocumentDefinition, PdfPlacement, SignatureInput, SignatureRole } from './types';

export { validatePdfBindings, validatePdfPlacementBinding, validateRequiredPdfSource } from './pdfBindingValidation';
export type { PdfBindingEvent, PdfBindingIssue } from './pdfBindingValidation';

export type PdfResolvedValue =
  | { kind: 'text'; text: string; available: boolean }
  | { kind: 'checkbox'; checked: boolean; available: boolean }
  | { kind: 'signature'; signature?: SignatureInput; available: boolean };
export type PdfSignatureContext = Partial<Record<SignatureRole, { signature: SignatureInput; signedOn: string }>>;
export interface PdfBindingContext {
  event: PdfBindingEvent;
  document: PdfDocumentDefinition;
  builtin: BuiltinAnswers;
  custom: Record<string, CustomAnswer>;
  /** Answers to the form's own questions. */
  documentAnswers: Record<string, CustomAnswer>;
  signatures?: PdfSignatureContext;
  /** The date the registration is submitted, for date-submitted boxes; the day the PDF is prepared. */
  submittedOn?: string;
}
export interface PdfBindingResult { values: Record<string, PdfResolvedValue>; issues: PdfBindingIssue[] }

const own = <T>(values: Record<string, T>, key: string): T | undefined => Object.hasOwn(values, key) ? values[key] : undefined;
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === 'string');
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Calendar formatting is deterministic across browser/Worker locales and time zones.
 * Dates without a year keep that distinction; a collected clock time is retained.
 */
export function formatPdfDate(value: string, format: NonNullable<PdfPlacement['dateFormat']> = 'short', includeYear = true): string {
  if (!value) return '';
  const [date, time, extra] = value.split('T');
  if (extra !== undefined || (time !== undefined && !/^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(time))) return '';
  if (!isValidDate(includeYear ? date : `2000-${date}`)) return '';
  if (format === 'iso') return value;
  const parts = date.split('-').map(Number);
  const year = includeYear ? parts[0] : undefined;
  const month = parts[includeYear ? 1 : 0], day = parts[includeYear ? 2 : 1];
  const text = format === 'long' ? `${MONTHS[month - 1]} ${day}${year === undefined ? '' : `, ${String(year).padStart(4, '0')}`}` : `${month}/${day}${year === undefined ? '' : `/${String(year).padStart(4, '0')}`}`;
  return text + (time ? ` ${time}` : '');
}

/** Check each of the form's own questions once, regardless of how many rectangles display its answer.
 * Preview permits missing answers; submission must use requireComplete=true.
 */
export function validateFormAnswers(document: PdfDocumentDefinition, answers: Record<string, CustomAnswer>, requireComplete = true): PdfBindingIssue[] {
  const issues: PdfBindingIssue[] = [];
  if (!record(answers)) return [{ code: 'invalid_answer', message: 'Form answers must be an object.' }];
  for (const key of Object.keys(answers)) if (!document.questions.some(question => question.id === key)) issues.push({ code: 'invalid_answer', questionId: key, message: 'This question is no longer on the form.' });
  for (const question of document.questions) {
    const answer = own(answers, question.id);
    // Preview shows unanswered questions as blank; only submission requires them.
    if (answer === undefined && !requireComplete) continue;
    const message = validatePdfQuestionAnswer(question, answer) ?? validateAnswer(requireComplete ? question : { ...question, required: false }, answer);
    if (message) issues.push({ code: 'invalid_answer', questionId: question.id, message });
  }
  return issues;
}

/** Additional checks close permissive legacy answer shapes before PDF projection.
 * Unanswered required registration fields are handled by registration validation.
 */
export function validatePdfQuestionAnswer(field: Field, answer: CustomAnswer | undefined): string | null {
  if (answer === undefined) return null;
  if (typeof answer === 'string' && !answer.trim() && !['checkboxes', 'choice_grid', 'checkbox_grid'].includes(field.type)) return null;
  switch (field.type) {
    case 'choice': case 'dropdown':
      if (typeof answer !== 'string' || (!(field.options ?? []).includes(answer) && !(field.type === 'choice' && field.allowOther && answer.trim()))) return 'Choose one of the configured options.';
      break;
    case 'checkboxes':
      if (!strings(answer)) return 'Supply a list of plain text answers.';
      break;
    case 'choice_grid': case 'checkbox_grid':
      if (!record(answer)) return 'Supply labeled grid answers.';
      break;
    case 'linear_scale':
      if (typeof answer !== 'string' || !/^(?:0|[1-9]\d*)$/.test(answer) || Number(answer) < (field.scale?.min ?? 1) || Number(answer) > (field.scale?.max ?? 5)) return 'Choose a configured scale value.';
      break;
    default:
      if (typeof answer !== 'string') return 'Supply a plain text answer.';
  }
  // Partial optional grid answers are useful during preview; their present cells still need validation.
  return validateAnswer({ ...field, required: false }, answer);
}

const ordered = (labels: string[], selected: string[]) => [...labels.filter(label => selected.includes(label)), ...selected.filter(label => !labels.includes(label))];
function questionText(field: Field, answer: CustomAnswer, placement: PdfPlacement): string {
  if (field.type === 'date') return formatPdfDate(answer as string, placement.dateFormat, field.includeYear !== false);
  if (field.type === 'time' && field.timeMode === 'duration') {
    const [hours, minutes, seconds] = (answer as string).split(':').map(Number);
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (field.type === 'rating' || field.type === 'linear_scale') {
    const source = placement.source;
    const fraction = source.kind === 'question' && source.projection.kind === 'text' && source.projection.numberFormat === 'fraction';
    return fraction ? `${Number(answer)} / ${field.type === 'rating' ? field.rating?.max ?? 5 : field.scale?.max ?? 5}` : String(Number(answer));
  }
  if (field.type === 'checkboxes') return ordered(field.options ?? [], answer as string[]).join(placement.multiline ? '\n' : ', ');
  if (field.type === 'choice_grid' || field.type === 'checkbox_grid') {
    const grid = field.grid!, rows = answer as Record<string, string | string[]>;
    return grid.rows.flatMap(row => {
      const selected = own(rows, row);
      if (selected === undefined || selected === '' || (Array.isArray(selected) && !selected.length)) return [];
      return [`${row}: ${Array.isArray(selected) ? ordered(grid.columns, selected).join(', ') : selected}`];
    }).join('\n');
  }
  return answerToText(field, answer);
}

interface SourceValue { value: string | boolean | SignatureInput; available: boolean }
const missing: SourceValue = { value: '', available: false };
/** Whether the participant is a minor on the day of signing: the day the forms are dated, or today until then. */
const minorWhenSigning = (context: PdfBindingContext) => isMinor(context.builtin.dateOfBirth, context.submittedOn ?? localTodayIso(), adultAgeOf(context.event));
function signatureValue(context: PdfBindingContext, role: SignatureRole): { signature: SignatureInput; signedOn: string } | undefined {
  const entry = context.signatures?.[role];
  if (!entry || !isValidDate(entry.signedOn)) return undefined;
  const signature = entry.signature;
  if (!signature || (signature.kind === 'typed' ? typeof signature.name !== 'string' || !signature.name.trim() : signature.kind !== 'drawn' || !(signature.png instanceof Uint8Array) || !signature.png.byteLength)) return undefined;
  return entry;
}

function resolveSource(context: PdfBindingContext, active: Record<string, CustomAnswer>, placement: PdfPlacement): SourceValue {
  const { event, document, builtin, documentAnswers } = context;
  const source = placement.source;
  switch (source.kind) {
    case 'literal': return { value: formatPdfDate(source.value, placement.dateFormat), available: true };
    // Never published: a field with nothing chosen blocks the form until one is.
    case 'unset': return { value: '', available: false };
    case 'builtin': {
      // The parent or guardian's name is only a minor's: an adult's emergency contact isn't one, so it stays blank.
      if (source.key === 'emergencyContact' && !minorWhenSigning(context)) return missing;
      const value = source.key === 'ward' ? participantWard(event, builtin) : source.key === 'participant' ? participantName(builtin)
        : source.key === 'fullAddress' ? fullAddress(builtin) : source.key === 'emergencyContactName' ? builtin.emergencyContact
        : own(builtin as unknown as Record<string, string | boolean | null>, source.key);
      if (value === undefined || value === null || value === '') return missing;
      if (PDF_BOOLEAN_BUILTINS.has(source.key)) return { value: value as boolean, available: true };
      return { value: source.key === 'dateOfBirth' ? formatPdfDate(value as string, placement.dateFormat) : String(value), available: !!String(value).trim() };
    }
    case 'event': {
      const value = source.key === 'dates' ? formatEventDatesLabel(event.dates) : event[source.key] ?? '';
      return { value, available: !!value.trim() };
    }
    case 'signature': {
      const entry = signatureValue(context, source.role);
      return entry ? { value: entry.signature, available: true } : missing;
    }
    case 'submittedDate': case 'parentSignedDate': {
      // A parent or guardian signs only for a minor; for an adult, their date stays blank.
      if (source.kind === 'parentSignedDate' && !minorWhenSigning(context)) return missing;
      const date = context.submittedOn && isValidDate(context.submittedOn) ? formatPdfDate(context.submittedOn, placement.dateFormat) : '';
      return date ? { value: date, available: true } : missing;
    }
    case 'question': {
      const { field, own: formQuestion } = pdfQuestion(event, document, source.fieldId)!, projection = source.projection;
      const answer = own(formQuestion ? documentAnswers : active, field.id);
      if (answer === undefined || (typeof answer === 'string' && !answer.trim()) || (Array.isArray(answer) && !answer.length)) return missing;
      if (projection.kind === 'optionSelected') {
        const label = field.options![field.optionIds!.indexOf(projection.optionId)];
        return { value: Array.isArray(answer) ? answer.includes(label) : answer === label, available: true };
      }
      // Other is the one answer that isn't among the question's options.
      if (projection.kind === 'otherText' || projection.kind === 'otherSelected') {
        const other = (Array.isArray(answer) ? answer : [answer]).find(value => typeof value === 'string' && value.trim() && !(field.options ?? []).includes(value)) as string | undefined;
        if (projection.kind === 'otherSelected') return { value: !!other, available: true };
        return other ? { value: other, available: true } : missing;
      }
      if (projection.kind === 'gridRowText' || projection.kind === 'gridCellSelected') {
        const row = field.grid!.rows[field.grid!.rowIds!.indexOf(projection.rowId)];
        const value = own(answer as Record<string, string | string[]>, row);
        if (value === undefined || value === '' || (Array.isArray(value) && !value.length)) return missing;
        if (projection.kind === 'gridRowText') return { value: Array.isArray(value) ? ordered(field.grid!.columns, value).join(placement.multiline ? '\n' : ', ') : value, available: true };
        const column = field.grid!.columns[field.grid!.columnIds!.indexOf(projection.columnId)];
        return { value: Array.isArray(value) ? value.includes(column) : value === column, available: true };
      }
      if (field.type === 'acknowledgment') return { value: answer === acknowledgmentValue(field), available: true };
      const text = questionText(field, answer, placement);
      return { value: text, available: !!text.trim() };
    }
  }
}

function blank(placement: PdfPlacement): PdfResolvedValue {
  return placement.kind === 'checkbox' ? { kind: 'checkbox', checked: false, available: false } : placement.kind === 'signature' ? { kind: 'signature', available: false } : { kind: 'text', text: '', available: false };
}

/** Pure shared projection for preview and final generation. Errors must block final output.
 * Skipped answers are filtered before inspection; only canonical placements select sources.
 * requireComplete enables required input/source/signature checks; review confirmations and
 * revision/asset/signature transport checks remain the submission layer’s responsibility.
 */
export function resolvePdfBindings(context: PdfBindingContext, { requireComplete = false } = {}): PdfBindingResult {
  const { event, document } = context;
  const issues = validatePdfBindings(event, document);
  if (!Array.isArray(document.questions) || !Array.isArray(document.placements)) return { values: {}, issues };
  const answerIssues = validateFormAnswers(document, context.documentAnswers, requireComplete);
  issues.push(...answerIssues);
  const active = activeQuestionAnswers(event.fields, context.custom);
  const values: Record<string, PdfResolvedValue> = Object.create(null);
  for (const placement of document.placements) {
    values[placement.id] = blank(placement);
    if (issues.some(issue => issue.placementId === placement.id)) continue;
    const source = placement.source;
    // Invalid metadata/source definitions may have only a document-level error.
    if (typeof pdfSourceType(event, document, source) !== 'string') continue;
    let error: string | null = null;
    if (source.kind === 'question') {
      // A form question's answer was checked above, and its problem is reported once, against the question.
      if (answerIssues.some(issue => issue.questionId === source.fieldId)) continue;
      const { field, own: formQuestion } = pdfQuestion(event, document, source.fieldId)!;
      if (!formQuestion) error = validatePdfQuestionAnswer(field, own(active, source.fieldId));
    }
    if (source.kind === 'builtin') {
      // A full name or address is made from several answers; each must be the expected type.
      for (const key of builtinSourceParts(source.key)) {
        const value = own(context.builtin as unknown as Record<string, unknown>, key);
        if (value !== undefined && value !== null && (PDF_BOOLEAN_BUILTINS.has(key) ? typeof value !== 'boolean' : typeof value !== 'string')) error = 'Supply the expected registration answer type.';
        else if (key === 'dateOfBirth' && typeof value === 'string' && value && !isValidDate(value)) error = 'Supply a valid date of birth.';
      }
    }
    if (source.kind === 'signature' && context.signatures?.[source.role] && !signatureValue(context, source.role)) error = 'Supply a valid signature and its ISO signing date.';
    if (error) { issues.push({ code: 'invalid_answer', placementId: placement.id, message: error }); continue; }
    const resolved = resolveSource(context, active, placement);
    if (!resolved.available) continue;
    if (placement.kind === 'checkbox') values[placement.id] = { kind: 'checkbox', checked: resolved.value as boolean, available: true };
    else if (placement.kind === 'signature') values[placement.id] = { kind: 'signature', signature: resolved.value as SignatureInput, available: true };
    // Text and date fields take only text and dates; a Yes or No answer fills a checkbox.
    else values[placement.id] = { kind: 'text', text: resolved.value as string, available: true };
  }
  if (requireComplete) {
    for (const role of pdfRequiredSigners(document, minorWhenSigning(context))) {
      if (!signatureValue(context, role)) issues.push({ code: 'invalid_answer', message: `Confirm the ${role} signature and signing date for this document.` });
    }
    for (const source of Array.isArray(document.requiredSources) ? document.requiredSources : []) {
      if (typeof pdfSourceType(event, document, source) !== 'string') continue;
      const placements = document.placements.filter(placement => typeof pdfSourceType(event, document, placement.source) === 'string' && samePdfSource(source, placement.source));
      if (!placements.length || placements.some(placement => !values[placement.id]?.available)) issues.push({ code: 'invalid_answer', message: 'Complete this required PDF source before submitting.' });
    }
  }
  return { values, issues };
}
